/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { DEVICE_CATALOG, generateBOM } from './utils/knxCalculator';

const C = {
  navy: '#0f1f3d',
  navyMid: '#162d52',
  navyLight: '#1e3a6e',
  navyBorder: '#253d6e',
  orange: '#F97316',
  white: '#ffffff',
  slate: '#94a3b8',
  slateLight: '#cbd5e1',
  green: '#22c55e',
  red: '#ef4444',
};

const EMAILJS_SERVICE_ID = 'service_3ozc5bt';
const EMAILJS_PUBLIC_KEY = 'QTpa1XU6aYrcSHnTm';
const QUOTE_TEMPLATE_ID = 'template_z9k3dq9';
const QUOTE_EMAIL = 'gil@briq-n-braq.com';

const PROJECT_TYPES = [
  { id: 'residential', label: 'Residential', icon: '🏠' },
  { id: 'commercial', label: 'Commercial', icon: '🏬' },
  { id: 'hotel', label: 'Hotel', icon: '🏨' },
  { id: 'office', label: 'Office', icon: '🏢' },
];

const DEVICE_ORDER = [
  'pushButtons', 'touchPanels', 'multiSensors', 'presenceSensors',
  'actuators', 'dimmers', 'blindActuators', 'ipRouters',
];

const GVS_PRODUCTS = {
  pushButtons: 'GVS KNX Push Button Sensor with Display 55mm',
  touchPanels: 'GVS KNX Smart Touch S7',
  multiSensors: 'GVS KNX Multifunction Sensor 9-in-1',
  presenceSensors: 'GVS KNX Presence Sensor Microwave',
  actuators: 'GVS 8CH Multifunction Actuator',
  dimmers: 'GVS 4CH Universal Dimming Actuator',
  blindActuators: 'KNX Blind/Shutter Actuator',
  ipRouters: 'KNX IP Router/Gateway',
};

const emptyDevices = () => Object.fromEntries(DEVICE_ORDER.map((k) => [k, 0]));

function Stepper({ value, min, max, onChange }) {
  // `draft` holds what the user is typing so the field can be cleared mid-edit;
  // it is null while the field mirrors `value`.
  const [draft, setDraft] = useState(null);
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const btn = (disabled) => ({
    width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.orange}`,
    background: C.navyLight, color: C.white, lineHeight: 1, padding: 0,
    fontSize: 22, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
  });
  const set = (v) => {
    setDraft(null);
    onChange(clamp(v));
  };
  const commit = () => {
    const n = parseInt(draft, 10);
    set(Number.isNaN(n) ? value : n);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button type="button" aria-label="Decrease" style={btn(value <= min)} onClick={() => set(value - 1)}>-</button>
      <input
        type="number"
        min={min}
        max={max}
        value={draft === null ? value : draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n) && n >= min && n <= max) onChange(n);
        }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{
          width: 56, height: 36, boxSizing: 'border-box', textAlign: 'center',
          fontSize: 18, fontWeight: 700, color: C.white, background: C.navy,
          border: `1px solid ${C.navyBorder}`, borderRadius: 8, padding: 0,
          MozAppearance: 'textfield',
        }}
      />
      <button type="button" aria-label="Increase" style={btn(value >= max)} onClick={() => set(value + 1)}>+</button>
    </div>
  );
}

function buildBomSummary(bom) {
  return bom.items.map((i) => `${i.name} x ${i.quantity}`).join('\n');
}

export default function KNXProjectCalculator() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [type, setType] = useState('residential');
  const [floors, setFloors] = useState(1);
  const [rooms, setRooms] = useState(5);
  const [devices, setDevices] = useState(emptyDevices);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [quoteEmail, setQuoteEmail] = useState('');
  const [quoteCompany, setQuoteCompany] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('idle'); // idle | sending | success | error

  const closeQuote = () => {
    setQuoteOpen(false);
    setQuoteStatus('idle');
  };

  const setQty = (key, qty) => setDevices((d) => ({ ...d, [key]: Math.max(0, qty) }));
  const totalDevices = DEVICE_ORDER.reduce((n, k) => n + devices[k], 0);
  const busLoad = DEVICE_ORDER.reduce((n, k) => n + devices[k] * DEVICE_CATALOG[k].mA, 0);

  const card = { background: C.navyMid, border: `1px solid ${C.navyBorder}`, borderRadius: 12, padding: 16 };
  const primaryBtn = (disabled) => ({
    background: disabled ? C.navyBorder : C.orange, color: C.white, border: 'none', borderRadius: 8,
    padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
  });
  const ghostBtn = {
    background: 'transparent', color: C.slateLight, border: `1px solid ${C.navyBorder}`,
    borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  };
  const smallLabel = { fontSize: 11, color: C.slate, textTransform: 'uppercase', letterSpacing: 1 };

  const stepLabels = ['Project Details', 'Devices', 'Results & BOM'];

  const header = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
      {stepLabels.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        return (
          <div key={label} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: active ? C.orange : C.navyMid, color: active ? C.white : C.slate,
            border: `1px solid ${active ? C.orange : C.navyBorder}`,
          }}>{n}. {label}</div>
        );
      })}
    </div>
  );

  let body;

  if (step === 1) {
    body = (
      <>
        <div style={{ ...card, marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, ...smallLabel }}>Project name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Harbour View Apartments"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
              background: C.navy, border: `1px solid ${C.navyBorder}`, color: C.white, fontSize: 14,
            }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          {PROJECT_TYPES.map((t) => {
            const active = type === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setType(t.id)} style={{
                ...card, cursor: 'pointer', textAlign: 'center',
                border: `2px solid ${active ? C.orange : C.navyBorder}`,
                color: active ? C.orange : C.white, fontWeight: 700, fontSize: 14,
              }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{t.icon}</div>
                {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={card}>
            <div style={{ ...smallLabel, marginBottom: 10 }}>Floors (1-10)</div>
            <Stepper value={floors} min={1} max={10} onChange={setFloors} />
          </div>
          <div style={card}>
            <div style={{ ...smallLabel, marginBottom: 10 }}>Rooms (1-100)</div>
            <Stepper value={rooms} min={1} max={100} onChange={setRooms} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={primaryBtn(!name.trim())} disabled={!name.trim()} onClick={() => setStep(2)}>Next →</button>
        </div>
      </>
    );
  } else if (step === 2) {
    body = (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
          {DEVICE_ORDER.map((key) => (
            <div key={key} style={{ ...card, border: `1px solid ${devices[key] > 0 ? C.orange : C.navyBorder}` }}>
              <div style={{ color: C.white, fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{DEVICE_CATALOG[key].name}</div>
              <div style={{ color: C.slate, fontSize: 11, marginBottom: 12 }}>{DEVICE_CATALOG[key].mA}mA per device</div>
              <Stepper value={devices[key]} min={0} max={999} onChange={(v) => setQty(key, v)} />
            </div>
          ))}
        </div>
        <div style={{ ...card, display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 24 }}>
          <div>
            <div style={smallLabel}>Total devices</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.white }}>{totalDevices}</div>
          </div>
          <div>
            <div style={smallLabel}>Estimated bus load</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.orange }}>{busLoad} mA</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" style={ghostBtn} onClick={() => setStep(1)}>← Back</button>
          <button type="button" style={primaryBtn(totalDevices === 0)} disabled={totalDevices === 0} onClick={() => setStep(3)}>Next →</button>
        </div>
      </>
    );
  } else {
    const bom = generateBOM({ projectType: type, floors, rooms, devices });
    const selected = DEVICE_ORDER.filter((k) => devices[k] > 0);
    const valid = bom.warnings.length === 0;
    const project = { name: name.trim(), type, floors, rooms };
    const cell = { padding: '8px 12px', borderBottom: `1px solid ${C.navyBorder}`, color: C.slateLight, fontSize: 13 };
    body = (
      <>
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.white, marginBottom: 6 }}>{project.name}</div>
          <div style={{ color: C.slate, fontSize: 13, textTransform: 'capitalize' }}>
            {type} · {floors} floor{floors > 1 ? 's' : ''} · {rooms} room{rooms > 1 ? 's' : ''}
          </div>
        </div>

        <div style={{ ...card, marginBottom: 16, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.navyLight }}>
                {['Component', 'Qty', 'Notes'].map((h) => (
                  <th key={h} style={{ ...cell, color: C.white, textAlign: h === 'Qty' ? 'center' : 'left', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bom.items.map((i) => (
                <tr key={i.key}>
                  <td style={{ ...cell, color: C.white }}>{i.name}</td>
                  <td style={{ ...cell, textAlign: 'center', color: C.orange, fontWeight: 700 }}>{i.quantity}</td>
                  <td style={cell}>{i.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...card, marginBottom: 16, border: `2px solid ${valid ? C.green : C.red}` }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: valid ? C.green : C.red, marginBottom: valid ? 0 : 8 }}>
            {valid ? '✅ VALID — topology within KNX limits' : '❌ Topology warnings'}
          </div>
          {!valid && (
            <ul style={{ margin: 0, paddingLeft: 20, color: C.slateLight, fontSize: 13 }}>
              {bom.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
            </ul>
          )}
        </div>

        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: C.white, fontSize: 15, marginBottom: 10 }}>GVS Recommended Products</div>
          {selected.map((k) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: `1px solid ${C.navyBorder}`, fontSize: 13, flexWrap: 'wrap' }}>
              <span style={{ color: C.slate }}>{DEVICE_CATALOG[k].name}</span>
              <span style={{ color: C.orange, fontWeight: 600 }}>{GVS_PRODUCTS[k]}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" style={ghostBtn} onClick={() => setStep(2)}>← Back</button>
          <button type="button" style={primaryBtn(false)} onClick={() => setQuoteOpen(true)}>Request Quote</button>
        </div>

        {quoteOpen && (
          <div
            onClick={closeQuote}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={async (e) => {
                e.preventDefault();
                setQuoteStatus('sending');
                try {
                  await emailjs.send(
                    EMAILJS_SERVICE_ID,
                    QUOTE_TEMPLATE_ID,
                    {
                      name: quoteName.trim(),
                      email: quoteEmail.trim(),
                      message: [
                        `Quote Request \u2014 ${project.name}`,
                        `Type: ${project.type} | Floors: ${project.floors} | Rooms: ${project.rooms} | Total Devices: ${bom.totalDevices}`,
                        '',
                        'Bill of Materials:',
                        buildBomSummary(bom),
                        '',
                        `Company: ${quoteCompany.trim() || '-'}`,
                        `Reply to: ${quoteEmail.trim()}`,
                      ].join('\n'),
                    },
                    EMAILJS_PUBLIC_KEY
                  );
                  setQuoteStatus('success');
                } catch {
                  setQuoteStatus('error');
                }
              }}
              style={{ ...card, background: C.navy, border: `2px solid ${C.orange}`, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}
            >
              {quoteStatus === 'success' ? (
                <>
                  <div style={{ color: C.green, fontWeight: 800, fontSize: 16, marginBottom: 16 }}>
                    ✅ Quote request sent! We will be in touch within 24 hours.
                  </div>
                  <button type="button" style={primaryBtn(false)} onClick={closeQuote}>Close</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.white, marginBottom: 4 }}>Request a Quote</div>
                  <div style={{ color: C.slate, fontSize: 13, marginBottom: 12 }}>{project.name}</div>
                  <div style={{ ...card, padding: 12, marginBottom: 16, fontSize: 12, color: C.slateLight }}>
                    {bom.items.map((i) => (
                      <div key={i.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span>{i.name}</span><span style={{ color: C.orange, fontWeight: 700 }}>x {i.quantity}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 8, color: valid ? C.green : C.red, fontWeight: 700 }}>
                      {bom.totalDevices} devices · {bom.busLoad}mA · {valid ? 'Topology VALID' : `${bom.warnings.length} warning(s)`}
                    </div>
                  </div>
                  {[
                    { label: 'Your Name', value: quoteName, set: setQuoteName, type: 'text', autoComplete: 'name', required: true },
                    { label: 'Your Email', value: quoteEmail, set: setQuoteEmail, type: 'email', autoComplete: 'email', required: true },
                    { label: 'Company Name', value: quoteCompany, set: setQuoteCompany, type: 'text', autoComplete: 'organization', required: false },
                  ].map((f) => (
                    <div key={f.label} style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 6, ...smallLabel }}>{f.label}{f.required ? ' *' : ' (optional)'}</label>
                      <input
                        required={f.required}
                        type={f.type}
                        autoComplete={f.autoComplete}
                        value={f.value}
                        onChange={(e) => f.set(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, background: C.navyMid, border: `1px solid ${C.navyBorder}`, color: C.white, fontSize: 14 }}
                      />
                    </div>
                  ))}
                  {quoteStatus === 'error' && (
                    <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>
                      Something went wrong. Please email {QUOTE_EMAIL} directly.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <button type="button" style={ghostBtn} onClick={closeQuote}>Cancel</button>
                    <button type="submit" style={primaryBtn(quoteStatus === 'sending')} disabled={quoteStatus === 'sending'}>
                      {quoteStatus === 'sending' ? 'Sending…' : 'Send Quote Request'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ background: C.navy, borderRadius: 12, padding: 24, color: C.white, minHeight: 400 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>🧮 KNX Project Calculator</h2>
      <p style={{ margin: '0 0 20px', color: C.slate, fontSize: 13 }}>
        Size your KNX bus, power supplies and couplers, and get a bill of materials.
      </p>
      {header}
      {body}
    </div>
  );
}
